import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Check demo mode synchronously to avoid flash of login page
  const initialDemoMode = localStorage.getItem('kliniktrack_demo_mode') === 'true';
  
  const [user, setUser] = useState(initialDemoMode ? { id: 'demo', email: 'demo@kliniktrack.com' } : null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(initialDemoMode ? {
    id: 'demo',
    email: 'demo@kliniktrack.com',
    first_name: 'Demo',
    last_name: 'User',
    organization: 'Kliniktrack Demo',
    role: 'demo'
  } : null);
  const [loading, setLoading] = useState(!initialDemoMode);
  const [isDemo, setIsDemo] = useState(initialDemoMode);

  useEffect(() => {
    // If already in demo mode, no need to check Supabase
    if (isDemo) {
      return;
    }

    let mounted = true;

    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (mounted && loading) {
        console.log('Auth loading timeout - forcing completion');
        setLoading(false);
      }
    }, 5000);

    // Get initial session
    const initSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (error) {
          console.error('Session error:', error);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          await fetchProfile(session.user.id, session.user.email);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Init session error:', error);
        if (mounted) setLoading(false);
      }
    };

    initSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log('Auth state change:', event);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          await fetchProfile(session.user.id, session.user.email);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId, userEmail) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
      }
      
      // Si pas de profil en base, créer un profil par défaut avec l'email
      if (!data && userEmail) {
        const defaultProfile = {
          id: userId,
          email: userEmail,
          first_name: userEmail.split('@')[0],
          last_name: '',
          organization: '',
          role: 'viewer'
        };
        setProfile(defaultProfile);
      } else {
        setProfile(data || null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loginAsDemo = () => {
    localStorage.setItem('kliniktrack_demo_mode', 'true');
    setIsDemo(true);
    setUser({ id: 'demo', email: 'demo@kliniktrack.com' });
    setProfile({
      id: 'demo',
      email: 'demo@kliniktrack.com',
      first_name: 'Demo',
      last_name: 'User',
      organization: 'Kliniktrack Demo',
      role: 'demo'
    });
    setLoading(false);
  };

  const signOut = async () => {
    try {
      if (isDemo) {
        localStorage.removeItem('kliniktrack_demo_mode');
        setIsDemo(false);
      } else {
        await supabase.auth.signOut();
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
    setUser(null);
    setSession(null);
    setProfile(null);
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signOut,
    loginAsDemo,
    isDemo,
    isAuthenticated: !!session || isDemo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
